import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error:', error, errorInfo);
  }

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      let recoveryAttempt = 0;
      try {
        recoveryAttempt = Number.parseInt(
          sessionStorage.getItem('novelflow_recovery_attempt') || '0',
          10,
        );
      } catch (e) {
        console.warn('Failed to read recovery attempt from sessionStorage:', e);
      }

      const handleRecover = () => {
        try {
          if (recoveryAttempt === 0) {
            // 初回エラー時は重要なデータを退避してクリア
            const history = localStorage.getItem('novelflow_reading_history');
            const customNovels = localStorage.getItem('novelflow_custom_novels');

            localStorage.clear();

            if (history) localStorage.setItem('novelflow_reading_history', history);
            if (customNovels) localStorage.setItem('novelflow_custom_novels', customNovels);

            sessionStorage.setItem('novelflow_recovery_attempt', '1');
          } else {
            // 2回目以降は全クリア
            localStorage.clear();
            sessionStorage.removeItem('novelflow_recovery_attempt');
          }
        } catch (e) {
          console.warn('Failed to clear storage during recovery:', e);
        }
        window.location.reload();
      };

      return (
        <div className="w-full h-full flex flex-col items-center justify-center p-6 text-center bg-stone-900 text-stone-200 select-text">
          <h2 className="text-lg font-bold font-serif mb-2 text-red-400">
            予期せぬエラーが発生しました
          </h2>
          <p className="text-xs opacity-60 max-w-xs mb-6 leading-relaxed">
            アプリのデータの読み込み、または描画処理中にエラーが発生しました。
          </p>
          <button
            type="button"
            onClick={handleRecover}
            className={`px-4 py-2 border rounded-md text-[11px] font-bold transition-colors ${
              recoveryAttempt === 0
                ? 'bg-stone-800 border-stone-700 hover:bg-stone-700'
                : 'bg-red-900/50 border-red-700 text-red-100 hover:bg-red-800/50'
            }`}
          >
            {recoveryAttempt === 0
              ? '設定などの一時データのみクリアして再読込'
              : '全データを初期化して再読込（※読書履歴等も削除されます）'}
          </button>
          {this.state.error && (
            <pre className="mt-6 p-3 bg-black/40 rounded border border-stone-800 text-[9px] text-left max-w-xs overflow-auto font-mono text-red-300">
              {this.state.error.toString()}
            </pre>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}
